import * as React from 'react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import {
  Przelewy24TenantSummary,
  useMutationMigratePrzelewy24ToManual,
  useMutationRefundPrzelewy24,
  useMutationSetPrzelewy24TenantBlocked,
  useMutationSyncPrzelewy24,
  useQueryPrzelewy24Tenants,
  useQueryPrzelewy24Transactions,
} from '@/modules/shared/query-client/usePrzelewy24Admin'

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('pl-PL')
}

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const d = new Date(/Z|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`)
  return isNaN(d.getTime()) ? iso : d.toLocaleString('pl-PL')
}

const formatAmount = (grosze: number, currency: string | null): string =>
  `${(grosze / 100).toFixed(2)} ${currency ?? 'PLN'}`

const billingModeLabel = (mode: string): string => {
  switch (mode) {
    case 'Prepaid6m':
      return '6 mies. z góry'
    case 'Prepaid12m':
      return '12 mies. z góry'
    case 'MonthlyCard':
      return 'mies. (karta)'
    default:
      return '—'
  }
}

const computeStatus = (
  t: Przelewy24TenantSummary,
): { label: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' } => {
  if (t.permanentlyBlocked) return { label: 'ZABLOKOWANA', tone: 'bad' }
  if (!t.subscriptionExpirationDate) return { label: 'BRAK SUB.', tone: 'neutral' }
  const now = Date.now()
  const expMs = new Date(t.subscriptionExpirationDate).getTime()
  if (expMs <= now) return { label: 'WYGASŁA', tone: 'bad' }
  if (!t.paid) return { label: 'NIEOPŁACONA', tone: 'warn' }
  const daysLeft = (expMs - now) / 86400_000
  if (daysLeft < 7) return { label: 'WKRÓTCE WYGASA', tone: 'warn' }
  return { label: 'AKTYWNA', tone: 'ok' }
}

const toneClasses: Record<'ok' | 'warn' | 'bad' | 'neutral', string> = {
  ok: 'tw-bg-green-100 tw-text-green-800',
  warn: 'tw-bg-amber-100 tw-text-amber-800',
  bad: 'tw-bg-red-100 tw-text-red-800',
  neutral: 'tw-bg-gray-100 tw-text-gray-700',
}

const txToneClasses: Record<string, string> = {
  Paid: 'tw-bg-green-100 tw-text-green-800',
  Registered: 'tw-bg-amber-100 tw-text-amber-800',
  Failed: 'tw-bg-red-100 tw-text-red-800',
}

export const Przelewy24BillingTab: React.FC = () => {
  const { data, isLoading, error, refetch } = useQueryPrzelewy24Tenants()
  const [filter, setFilter] = useState('')

  const rows = useMemo<Przelewy24TenantSummary[]>(() => {
    if (!data) return []
    const q = filter.trim().toLowerCase()
    if (!q) return data
    return data.filter(
      (t) =>
        (t.companyName ?? '').toLowerCase().includes(q) ||
        t.tenantId.toLowerCase().includes(q) ||
        (t.nip ?? '').toLowerCase().includes(q),
    )
  }, [data, filter])

  if (isLoading) {
    return <div className="tw-p-6 tw-text-gray-500">Ładowanie firm…</div>
  }
  if (error) {
    const status = (error as any)?.response?.status
    const message =
      status === 403
        ? 'Brak uprawnień. Wymagane konto globalnego administratora.'
        : 'Nie udało się załadować listy firm.'
    return (
      <div className="tw-p-6 tw-text-red-600">
        {message}{' '}
        <button onClick={() => refetch()} className="tw-underline">
          Spróbuj ponownie
        </button>
      </div>
    )
  }

  return (
    <div className="tw-p-6">
      <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-4">
        <div>
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Przelewy24</h3>
          <p className="tw-text-sm tw-text-gray-600">
            Firmy rozliczane samoobsługowo przez Przelewy24 (przedpłata 6/12 mies.). Pola
            billingowe ustawiają powiadomienia P24 — „Synchronizuj" ponownie pobiera stan
            transakcji z P24 i aktywuje firmę, jeśli płatność została zaksięgowana.
          </p>
        </div>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtruj po nazwie, ID, NIP…"
          className="tw-w-72 tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
        />
      </div>

      <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200">
        <table className="tw-w-full tw-text-sm">
          <thead className="tw-bg-gray-50 tw-text-gray-600 tw-uppercase tw-text-xs">
            <tr>
              <Th>Firma</Th>
              <Th>Pakiet</Th>
              <Th>Tryb</Th>
              <Th>Wygasa</Th>
              <Th>Status</Th>
              <Th>Ostatnia płatność</Th>
              <Th className="tw-text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="tw-py-6 tw-text-center tw-text-gray-500">
                  Brak firm pasujących do filtra.
                </td>
              </tr>
            )}
            {rows.map((t) => (
              <TenantRow key={t.tenantId} tenant={t} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const TenantRow: React.FC<{ tenant: Przelewy24TenantSummary }> = ({ tenant: t }) => {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const sync = useMutationSyncPrzelewy24()
  const refund = useMutationRefundPrzelewy24()
  const migrate = useMutationMigratePrzelewy24ToManual()
  const setBlocked = useMutationSetPrzelewy24TenantBlocked()
  const confirm = useConfirm()
  const status = computeStatus(t)
  const companyLabel = t.companyName ?? t.tenantId

  const onRefund = async () => {
    const ok = await confirm({
      title: 'Zlecić zwrot płatności?',
      message: `Zwrócona zostanie ostatnia zapłacona transakcja firmy „${companyLabel}". Dostęp zostanie zablokowany po rozliczeniu zwrotu przez Przelewy24.`,
      confirmText: 'Zwróć',
      cancelText: 'Anuluj',
    })
    if (ok) refund.mutate(t.tenantId)
  }

  const onMigrate = async () => {
    const ok = await confirm({
      title: 'Przenieść firmę na panel Claro?',
      message: `Firma „${companyLabel}" przestanie być rozliczana samoobsługowo przez Przelewy24.`,
      content: (
        <ul className="tw-mt-3 tw-list-disc tw-pl-5 tw-space-y-1 tw-text-sm tw-text-content-base/70">
          <li>
            dostęp zależy odtąd od statusu faktur w panelu Claro dla NIP{' '}
            <span className="tw-font-semibold">{t.nip ?? '(brak NIP!)'}</span> — firma musi być
            w panelu założona, inaczej zostanie zablokowana (402)
          </li>
          <li>koniec z blokadą po wygaśnięciu abonamentu i z automatycznymi zadaniami P24</li>
          <li>niezakończone płatności P24 zostaną anulowane</li>
          <li>blokada z rejestracji zostanie zdjęta</li>
          <li>pola abonamentu (pakiet, opłacony, data wygaśnięcia) ustawiasz odtąd ręcznie w „Edytuj"</li>
        </ul>
      ),
      confirmText: 'Przenieś',
      cancelText: 'Anuluj',
    })
    if (ok) migrate.mutate(t.tenantId)
  }

  const onToggleBlocked = async () => {
    if (t.permanentlyBlocked) {
      const ok = await confirm({
        title: 'Odblokować firmę?',
        message:
          `Firma „${companyLabel}" dostanie dostęp bez opłaconej płatności Przelewy24 — ` +
          'do czasu, aż wygaśnie data ważności abonamentu (jeśli jest ustawiona).',
        confirmText: 'Odblokuj',
        cancelText: 'Anuluj',
      })
      if (!ok) return
    }
    setBlocked.mutate({ tenantId: t.tenantId, blocked: !t.permanentlyBlocked })
  }

  const migrateMessage = (() => {
    if (migrate.isPending) return null
    if (migrate.isError) {
      const message = (migrate.error as any)?.response?.data?.message
      return { text: message ?? 'Nie udało się przenieść firmy.', tone: 'bad' as const }
    }
    if (!migrate.data?.migrated) return null
    const children = migrate.data.migratedChildren
    return {
      text:
        'Przeniesiono na panel Claro' +
        (children > 0 ? ` (wraz z ${children} firmami konta usługowego)` : '') +
        (migrate.data.unblocked ? ', blokada rejestracyjna zdjęta' : '') +
        '. Sprawdź, czy firma jest w panelu.',
      tone: 'ok' as const,
    }
  })()

  const syncMessage = (() => {
    if (sync.isPending) return null
    if (sync.isError) return { text: 'Błąd synchronizacji.', tone: 'bad' as const }
    const r = sync.data
    if (!r) return null
    if (r.activated || r.alreadyProcessed)
      return { text: 'Zsynchronizowano — firma aktywna.', tone: 'ok' as const }
    if (r.pending) return { text: 'Płatność jeszcze niezaksięgowana w P24.', tone: 'warn' as const }
    if (r.failed) return { text: `Niepowodzenie: ${r.notes ?? 'błąd'}.`, tone: 'bad' as const }
    return null
  })()

  return (
    <>
      <tr className="tw-border-t tw-border-gray-100 hover:tw-bg-gray-50">
        <Td>
          <div className="tw-font-medium tw-text-gray-900">{t.companyName ?? '—'}</div>
          <div className="tw-text-xs tw-text-gray-500">{t.nip ? `NIP: ${t.nip}` : 'brak NIP'}</div>
          <div className="tw-text-xs tw-text-gray-500 tw-font-mono">{t.tenantId}</div>
        </Td>
        <Td>{t.planCode ?? '—'}</Td>
        <Td>{billingModeLabel(t.billingMode)}</Td>
        <Td>{formatDate(t.subscriptionExpirationDate)}</Td>
        <Td>
          <span
            className={`tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${toneClasses[status.tone]}`}
          >
            {status.label}
          </span>
        </Td>
        <Td>{formatDate(t.paymentDate)}</Td>
        <Td className="tw-text-right tw-whitespace-nowrap">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="tw-mr-2 tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-gray-700 tw-cursor-pointer hover:tw-bg-gray-100"
          >
            {expanded ? 'Ukryj' : 'Transakcje'}
          </button>
          <button
            onClick={() => sync.mutate(t.tenantId)}
            disabled={sync.isPending}
            className="tw-mr-2 tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer tw-bg-emerald-600 hover:tw-bg-emerald-700 disabled:tw-opacity-50"
          >
            {sync.isPending ? 'Synchronizuję…' : 'Synchronizuj'}
          </button>
          <button
            onClick={() => navigate(`/company/${t.tenantId}/administrationPanel/editCompany`)}
            className="tw-mr-2 tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer tw-bg-blue-600 hover:tw-bg-blue-700"
          >
            Edytuj
          </button>
          <button
            onClick={onToggleBlocked}
            disabled={setBlocked.isPending}
            title={
              t.permanentlyBlocked
                ? 'Zdejmij blokadę — firma dostanie dostęp bez opłaty P24'
                : 'Zablokuj dostęp do firmy'
            }
            className={`tw-mr-2 tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer disabled:tw-opacity-50 ${
              t.permanentlyBlocked
                ? 'tw-bg-emerald-600 hover:tw-bg-emerald-700'
                : 'tw-bg-red-600 hover:tw-bg-red-700'
            }`}
          >
            {t.permanentlyBlocked ? 'Odblokuj' : 'Zablokuj'}
          </button>
          <button
            onClick={onMigrate}
            disabled={migrate.isPending}
            title="Przenieś firmę na rozliczenie fakturami przez panel Claro"
            className="tw-mr-2 tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer tw-bg-indigo-600 hover:tw-bg-indigo-700 disabled:tw-opacity-50"
          >
            {migrate.isPending ? 'Przenoszę…' : 'Na panel Claro'}
          </button>
          <button
            onClick={onRefund}
            disabled={refund.isPending}
            className="tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer tw-bg-red-600 hover:tw-bg-red-700 disabled:tw-opacity-50"
          >
            {refund.isPending ? 'Zwracam…' : 'Zwróć'}
          </button>
          {refund.isError && (
            <div className="tw-mt-1 tw-text-xs tw-bg-red-100 tw-text-red-800 tw-inline-block tw-rounded tw-px-2 tw-py-0.5">
              Zwrot nie powiódł się.
            </div>
          )}
          {refund.data?.refunded && (
            <div className="tw-mt-1 tw-text-xs tw-bg-green-100 tw-text-green-800 tw-inline-block tw-rounded tw-px-2 tw-py-0.5">
              Zlecono zwrot — dostęp zostanie zablokowany po rozliczeniu w P24.
            </div>
          )}
          {migrateMessage && (
            <div
              className={`tw-mt-1 tw-text-xs ${toneClasses[migrateMessage.tone]} tw-inline-block tw-rounded tw-px-2 tw-py-0.5`}
            >
              {migrateMessage.text}
            </div>
          )}
          {syncMessage && (
            <div className={`tw-mt-1 tw-text-xs ${toneClasses[syncMessage.tone]} tw-inline-block tw-rounded tw-px-2 tw-py-0.5`}>
              {syncMessage.text}
            </div>
          )}
        </Td>
      </tr>
      {expanded && (
        <tr className="tw-bg-gray-50">
          <td colSpan={7} className="tw-px-3 tw-py-3">
            <TransactionsPanel tenantId={t.tenantId} />
          </td>
        </tr>
      )}
    </>
  )
}

const TransactionsPanel: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const { data, isLoading, error } = useQueryPrzelewy24Transactions(tenantId)

  if (isLoading) return <div className="tw-text-gray-500 tw-text-xs">Ładowanie transakcji…</div>
  if (error) return <div className="tw-text-red-600 tw-text-xs">Nie udało się załadować transakcji.</div>
  if (!data || data.length === 0)
    return <div className="tw-text-gray-500 tw-text-xs">Brak transakcji P24 dla tej firmy.</div>

  return (
    <table className="tw-w-full tw-text-xs tw-bg-white tw-rounded tw-border tw-border-gray-200">
      <thead className="tw-bg-gray-100 tw-text-gray-600 tw-uppercase">
        <tr>
          <Th>Utworzono</Th>
          <Th>Pakiet</Th>
          <Th>Okres</Th>
          <Th>Kwota</Th>
          <Th>OrderId</Th>
          <Th>Status</Th>
          <Th>Zaksięgowano</Th>
          <Th>SessionId</Th>
        </tr>
      </thead>
      <tbody>
        {data.map((tx) => (
          <tr key={tx.id} className="tw-border-t tw-border-gray-100">
            <Td>{formatDateTime(tx.createdAt)}</Td>
            <Td>{tx.planCode ?? '—'}</Td>
            <Td>{tx.months} mies.</Td>
            <Td>{formatAmount(tx.amountGrosze, tx.currency)}</Td>
            <Td>{tx.orderId ?? '—'}</Td>
            <Td>
              <span
                className={`tw-rounded tw-px-2 tw-py-0.5 tw-font-semibold ${txToneClasses[tx.status] ?? 'tw-bg-gray-100 tw-text-gray-700'}`}
              >
                {tx.status}
              </span>
            </Td>
            <Td>{formatDateTime(tx.finalizedAt)}</Td>
            <Td className="tw-font-mono tw-text-gray-500">{tx.sessionId}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <th className={`tw-text-left tw-px-3 tw-py-2 tw-font-semibold ${className ?? ''}`}>{children}</th>
)
const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <td className={`tw-px-3 tw-py-2 tw-align-top ${className ?? ''}`}>{children}</td>
)
